// Shared types + size/anchor constants for the `context` command surface.
// Extracted from commands/context.ts so the renderers, validators and the
// dispatcher share one definition instead of a 1.7k-line monolith.

// Size limits aligned with API hard caps and infra practical ceiling (~5-8KB
// before Cloudflare/Render rejects). The content cap is the shared one so the
// CLI pre-flight and the API server-side guard can never disagree.
export const MAX_FIELD_SIZE = 32 * 1024 // 32KB per --gotcha/--decision/--invariant/--check
export { MAX_CONTENT_BYTES as MAX_CONTENT_SIZE } from '@driftless/topic-schema'

// Anchoring threshold + noise filter come from the shared lib so the CLI's
// local validator and the API's server-side anchor validation can never drift
// on what counts as over-broad ("src/**" on a 2k-file repo → useless catch-all)
// or as noise. Soft signals — never blocking. See [[structural-feedback-loops]].
export { OVERBROAD_PATTERN_THRESHOLD, NOISE_PATH } from '@driftless/topic-schema'

export interface CanonicalSummary {
  topic: string
  name: string
  summary: string
  badges: string[]
  anchors: { files: string[]; patterns: string[]; repos: string[] }
  classification?: {
    status: string
  }
  stale: { is_stale: boolean; reason: string | null }
  content?: string
  tags?: string[]
  metadata: { created_by: string | null; last_updated: string | null; source: string }
}

export interface TopicRelation {
  id: string
  to_slug?: string
  from_slug?: string
  relation_type: string
  created_at: string
}

export interface CanonicalContext {
  topic: string
  summary: string
  version?: number
  description: {
    what: string | null
    how: string | null
    decisions: string | null
    gotchas: string[]
    ownership: string | null
  }
  // Arc 2 — topic graph wiki-links. `references_topics` is the forward
  // direction parsed from [[slug]] mentions; `referenced_by` is the
  // reverse direction computed by the API. Older APIs (pre-0.1.67) may
  // omit both — render code guards with `?? []`.
  references_topics?: string[]
  referenced_by?: string[]
  anchors: {
    files: string[]
    patterns: string[]
    repos: string[]
    repo_names?: Record<string, string>
    docs: Array<{ path: string | null; content: string }>
  }
  // ARP v2 — unified typed anchors + structured rules (optional for back-compat
  // with an older API that doesn't emit them yet).
  refs?: Array<{ type: string; value: string; [k: string]: unknown }>
  rules?: Array<Record<string, unknown>>
  // `components` was populated by the retired code parser. The API
  // omits it entirely after the Component table drop; treat as optional
  // so the renderer doesn't crash on `.length` post-update.
  components?: Array<{ type: string; name: string; file_path: string; line_number: number | null; repo_id?: string | null }>
  invariants: string[]
  events: Array<{ source: 'topic' | 'context'; event_type: string; commit_hash: string | null; author: string | null; detail: string | null; created_at: string }>
  required_checks: string[]
  classification?: {
    status: string
  }
  stale: { is_stale: boolean; reason: string | null }
  content?: string
  tags?: string[]
  metadata: { created_by: string | null; last_updated: string | null; source: string }
}

export interface MatchFilesResult {
  context: CanonicalContext
  history: unknown[]
  match_reason: string
}

// Stats returned by the pattern validator. Telemetered as
// cli_pattern_validation so we can measure friction: how often agents
// hand us a pattern that misses on the first try, by what failure mode.
export interface PatternValidationStats {
  ok: boolean
  pattern_count: number
  zero_match_count: number
  noise_only_count: number
  over_broad_count: number
  max_match_count: number
}
